<template>
  <v-row row wrap>
    <v-col cols="12">
      <div class="table-fixed-header-wrap">
      <v-data-table class="mt-3" fixed-header height="calc(100vh - 260px)" :headers="headers" :items="items" :loading="loading"
        :footer-props="{ itemsPerPageOptions: [5, 10, 25, 50] }">
        <template slot="no-data">
          <div class="text-xs-center" v-t="'common.noData'"></div>
        </template>

        <template slot="loading">
          <div class="text-xs-center" v-t="'common.loading'"></div>
        </template>

        <template v-slot:item.updatedAt="{ item }">
          {{ formatDate(item.updatedAt || item.lastAccess || item.createdAt) }}
        </template>

        <template v-slot:item.action="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                color="primary"
                class="mx-1"
                v-bind="attrs"
                v-on="on"
                @click="download(item)"
              >
                mdi-download
              </v-icon>
            </template>
            <span>{{ $t('media.file.csvEtiquetas.download') }}</span>
          </v-tooltip>
          <delete-button @click="confirmDelete(item)" />
        </template>
      </v-data-table>
      </div>

      <v-dialog v-model="deleteDialog" max-width="400" persistent>
        <v-card>
          <v-card-title class="text-h6">{{ $t('media.file.csvEtiquetas.deleteConfirm') }}</v-card-title>
          <v-card-text>
            {{ itemToDelete ? itemToDelete.filename : '' }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="deleteDialog = false">{{ $t('common.cancel') }}</v-btn>
            <v-btn color="error" :loading="deleting" @click="doDelete">{{ $t('common.delete') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-col>
  </v-row>
</template>

<script>
import FileProvider from '../../../providers/FileProvider'
import moment from 'moment-timezone'
import { DeleteButton } from '@dracul/common-frontend'

export default {
  name: 'CsvEtiquetasList',
  components: { DeleteButton },
  props: {
    companyId: { type: String, default: null },
    depositoId: { type: String, default: null }
  },
  data() {
    return {
      items: [],
      loading: false,
      downloading: null,
      deleting: null,
      deleteDialog: false,
      itemToDelete: null
    }
  },
  computed: {
    headers() {
      return [
        { text: this.$t('media.file.filename'), value: 'filename', sortable: true },
        { text: this.$t('media.file.csvEtiquetas.lastUpdate'), value: 'updatedAt', sortable: true },
        { text: this.$t('common.actions'), value: 'action', sortable: false }
      ]
    }
  },
  watch: {
    companyId: 'fetch',
    depositoId: 'fetch'
  },
  created() {
    if (this.companyId && this.depositoId) {
      this.fetch()
    }
  },
  methods: {
    fetch() {
      if (!this.companyId || !this.depositoId) {
        this.items = []
        return
      }
      this.loading = true
      FileProvider.csvEtiquetasList(this.companyId, this.depositoId)
        .then((r) => {
          this.items = r.data?.csvEtiquetasList || []
        })
        .catch((err) => {
          this.items = []
          this.$emit('flash', {
            message: err.message || this.$t('media.file.csvEtiquetas.errorList'),
            color: 'error'
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async download(file) {
      this.downloading = file.filename
      try {
        const companyId = file.companyId || this.companyId
        const depositoId = file.depositoId || this.depositoId
        const res = await FileProvider.csvEtiquetasDownloadToken(
          companyId,
          depositoId,
          file.filename
        )
        const token = res?.data?.csvEtiquetasDownloadToken
        if (!token) {
          this.$emit('flash', {
            message: this.$t('media.file.csvEtiquetas.errorToken'),
            color: 'error'
          })
          return
        }
        const baseUrl =
          (process.env.VUE_APP_APIHOST && process.env.VUE_APP_APIHOST.replace(/\/$/, '')) ||
          window.location.origin
        const url = `${baseUrl}/media/csv/${companyId}/${depositoId}/${encodeURIComponent(file.filename)}?token=${encodeURIComponent(token)}`
        window.open(url, '_blank', 'noopener,noreferrer')
        this.$emit('flash', {
          message: this.$t('media.file.csvEtiquetas.downloadStarted'),
          color: 'success'
        })
      } catch (e) {
        this.$emit('flash', {
          message: e.message || this.$t('media.file.csvEtiquetas.errorDownload'),
          color: 'error'
        })
      } finally {
        this.downloading = null
      }
    },
    confirmDelete(item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },
    async doDelete() {
      if (!this.itemToDelete || !this.companyId || !this.depositoId) return
      this.deleting = this.itemToDelete.filename
      try {
        await FileProvider.csvEtiquetasDelete(
          this.companyId,
          this.depositoId,
          this.itemToDelete.filename
        )
        this.$emit('flash', {
          message: this.$t('media.file.csvEtiquetas.deleted'),
          color: 'success'
        })
        this.deleteDialog = false
        this.itemToDelete = null
        this.fetch()
      } catch (e) {
        this.$emit('flash', {
          message: e.message || this.$t('media.file.csvEtiquetas.errorDelete'),
          color: 'error'
        })
      } finally {
        this.deleting = null
      }
    },
    formatDate(value) {
      if (value == null || value === '') return '-'
      // Backend puede enviar timestamp en ms como string (ej. "1770254803851")
      const parsed = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value
      const m = moment(parsed)
      if (!m.isValid()) return '-'
      return m.tz('America/Argentina/Buenos_Aires').format('DD-MM-YYYY HH:mm')
    }
  }
}
</script>

<style scoped>
.table-fixed-header-wrap {
  min-height: 0;
}
</style>
